import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { API, errMsg } from './careApi';
import { Btn, Input } from './careUi';

export default function CareOwnerSetup() {
  const token = localStorage.getItem('token');
  const [state, setState] = useState(token ? 'checking' : 'login');
  const [owner, setOwner] = useState('');
  const [f, setF] = useState({ email: 'care.admin@staymii.com', name: 'Care Admin', password: '', confirm: '' });
  const [err, setErr] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [newOrg, setNewOrg] = useState('');
  const [busy, setBusy] = useState(false);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/owner-check`, H)
      .then((r) => {
        setOwner(r.data.email); setState(r.data.allowed ? 'form' : 'denied');
        if (r.data.allowed) axios.get(`${API}/auth/owner-orgs`, H).then((o) => { setOrgs(o.data); setOrgId(o.data[0]?.id || '__new'); });
      })
      .catch(() => setState('login'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (f.password.length < 8) return setErr('Password must be at least 8 characters');
    if (f.password !== f.confirm) return setErr('Passwords do not match');
    setBusy(true);
    if (orgId === '__new' && !newOrg.trim()) return setErr('Enter the new company name');
    try {
      await axios.post(`${API}/auth/owner-setup`, { email: f.email, name: f.name, password: f.password, org_id: orgId === '__new' ? null : orgId, new_org_name: orgId === '__new' ? newOrg : null }, H);
      setState('done');
    }
    catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="care-owner-setup">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-7">
        <p className="text-xs font-semibold text-teal-700 tracking-wide">STAYMII CARE</p>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">Set up Care admin</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">For StayMii platform owners only. Create or reset the StayMii Care admin login.</p>
        {state === 'checking' && <Loader2 className="w-5 h-5 animate-spin text-teal-700" />}
        {state === 'login' && (
          <div data-testid="owner-setup-login-required">
            <p className="text-sm text-slate-700">Please log in to your StayMii owner account first, then come back to this page.</p>
            <Link to="/auth/login" className="inline-block mt-4"><Btn data-testid="owner-setup-go-login">Log in to StayMii</Btn></Link>
          </div>
        )}
        {state === 'denied' && (
          <p className="text-sm text-rose-700 flex gap-2" data-testid="owner-setup-denied"><ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />{owner} is not a StayMii platform owner account.</p>
        )}
        {state === 'form' && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-xs text-slate-500">Signed in as <b>{owner}</b></p>
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1">Company (nursing home)</span>
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)} data-testid="owner-setup-org" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} — /care/register/{o.slug}</option>)}
                <option value="__new">+ Add new company…</option>
              </select>
            </label>
            {orgId === '__new' && <Input label="New company name" value={newOrg} onChange={(e) => setNewOrg(e.target.value)} testid="owner-setup-new-org" />}
            <Input label="Care admin email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="owner-setup-email" />
            <p className="text-[11px] text-slate-400 -mt-2">If this email already belongs to a Care user, it becomes an active Admin with the new password.</p>
            <Input label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="owner-setup-name" />
            <Input label="New password (min 8 characters)" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="owner-setup-password" />
            <Input label="Confirm password" type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} testid="owner-setup-confirm" />
            {err && <p className="text-sm text-rose-600" data-testid="owner-setup-error">{err}</p>}
            <Btn type="submit" loading={busy} className="w-full" data-testid="owner-setup-submit">Save Care admin login</Btn>
          </form>
        )}
        {state === 'done' && (
          <div data-testid="owner-setup-done">
            <p className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Care admin login saved for {f.email}.</p>
            <Link to="/care/login" className="inline-block mt-4"><Btn data-testid="owner-setup-go-care-login">Go to Care sign in</Btn></Link>
          </div>
        )}
      </div>
    </div>
  );
}
